import React, {FC, useEffect, useRef, useState} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '@vio/atlas-tokens'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

import {ButtonLink} from '../../ButtonLink'
interface Props {
  /** Amount of lines to what content must be truncated */
  lines: number
  /** We can override default click behaviour and cancel expanding by passing `onReadMoreClick` handler */
  onReadMoreClick?: () => void
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  gap: ${cssTheme.layout.spacing.s300};
`

const Content = styled.div<{lines: number}>`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: ${({lines}) => lines ?? ''};
  -webkit-box-orient: vertical;
  white-space: initial;
  &.--is-expanded {
    display: block;
  }
`

const FullHeightContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  opacity: 0;
  pointer-events: none;
`

export const TruncatedContent: FC<Props> = ({
  children,
  lines,
  onReadMoreClick
}) => {
  const isHydrated = useIsHydrated()
  const [isExpanded, setIsExpanded] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const fullHeightContentRef = useRef<HTMLDivElement>(null)

  const handleExpandClick = () => {
    if (onReadMoreClick) {
      onReadMoreClick()
      return
    }
    setIsExpanded(true)
  }

  useEffect(() => {
    const fullHeightContent = fullHeightContentRef.current
    const content = contentRef.current
    if (
      fullHeightContent &&
      content &&
      fullHeightContent.clientHeight === content.clientHeight
    ) {
      setIsExpanded(true)
    }
  }, [])

  return (
    <Wrapper>
      <Content
        lines={lines}
        className={cx(isExpanded && '--is-expanded')}
        ref={contentRef}
      >
        {children}
      </Content>
      {/* Create invisible absolute positioned block to measure full content height */}
      <FullHeightContent ref={fullHeightContentRef}>
        {children}
      </FullHeightContent>
      {isHydrated && !isExpanded && (
        <ButtonLink
          size="sm"
          variant="primary"
          isCompact
          onClick={handleExpandClick}
        >
          <FormattedMessageWrapper defaultMessage="Read more" id="readMore" />
        </ButtonLink>
      )}
    </Wrapper>
  )
}
